import * as v from "valibot"
import { useTranslation } from "./translation"

const { t } = useTranslation()

export const PhoneNumberValidator = v.custom<string>(
	input => typeof input === "string" && /^(?:\+989|09)\d{9}$/.test(input),
	() => t("error.phoneNumber"),
)

export const FloatValidator = v.custom<string>(
	input => (typeof input === "string") && /^(?:\d*\.)?\d+$/.test(input.toString()),
	() => t("error.number"),
)

export const SignedFloatValidator = v.custom<string>(
	input => (typeof input === "string") && /^-?(?:\d*\.)?\d+$/.test(input.toString()),
	() => t("error.number"),
)

export const IntegerValidator = v.custom<string>(
	input => (typeof input === "string") && /^\d*$/.test(input.toString()),
	() => t("error.integer"),
)

export const SignedIntegerValidator = v.custom<string>(
	input => (typeof input === "string") && /^-?\d*$/.test(input.toString()),
	() => t("error.integer"),
)

export const useValidator = defineComposable("validator", () => {
	const { t } = useTranslation()

	function integer(input: string) {
		if (!input)
			return null
		const valid = /^-?(?:0|[1-9]\d*)$/.test(input)
		return valid || t("error.integer")
	}

	function hasPersianWord(input: string) {
		if (!input)
			return null

		const hasPersian = /[\u0600-\u06FF]/.test(input)

		if (!hasPersian)
			return true
		else
			return t("error.isPersian")
	}

	function postalCode(input: string) {
		if (!input)
			return null

		const valid = /^[13-9]{5}\d{5}$/.test(input)

		return valid || t("error.postalCode")
	}

	function percentage(input: string) {
		if (!input)
			return null
		const isNumber = integer(input)
		const valid = isNumber && input <= "100" && input >= "0"
		return valid || t("error.percentage")
	}

	function time(input: string) {
		if (!input)
			return null
		const valid = /^(?:[01]?\d|2[0-3]):[0-5]\d$/.test(input)
		return valid ? null : t("error.time")
	}

	function number(input: string) {
		if (!input)
			return null
		const valid = /^(?:\d*\.)?\d+$/.test(input)
		return valid || t("error.number")
	}

	function email(input: string) {
		if (!input)
			return true
		const valid = /\S[^\s@]*@\S+\.\S+/.test(input)
		return valid || t("error.email")
	}

	function phoneNumber(input: string) {
		if (!input)
			return null
		const valid = /^(?:\+989|09)\d{9}$/.test(input)
		return valid || t("error.phoneNumber")
	}

	function telephoneSabet(input: string) {
		if (!input)
			return null
		const valid = /^0[1-8]{2}\d{8}$/.test(input)
		const length = (input.length === 11)
		return (length && valid) || t("error.telephoneSabet")
	}

	function password(input: string) {
		if (!input)
			return null
		const valid = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/.test(input)
		return valid || t("error.password")
	}

	function validSSN(input: string) {
		let result = false
		if (/^\d{10}$/.test(input)) {
			const check = Number.parseInt(input[9]!)
			let sum = 0
			for (let i = 0; i < 9; ++i)
				sum += Number.parseInt(input[i]!) * (10 - i)

			sum %= 11
			result = (sum < 2 && check === sum) || (sum >= 2 && check + sum === 11)
		}

		return result || t("error.SSN")
	}

	function date(input: string) {
		if (!input)
			return true
		const valid = /^\d{4}\/(?:0[1-9]|1[012])\/(?:0[1-9]|[12]\d|3[01])$/.test(input)
		return valid || t("error.date")
	}

	function required(value: any) {
		return (value !== null && value !== "" && value !== undefined) || t("error.required")
	}

	function passwordRepeat(label: string) {
		return (value: string, { form }: { form: Record<string, any> }) => {
			return value === form[label] || t("error.passwordRepeat")
		}
	}

	function minCharacterCount(minChar: number) {
		return (value: string) => {
			return value.length >= minChar || `${t("error.minCharacterCountStart")} [${minChar}] ${t("error.minCharacterCountEnd")}`
		}
	}

	function exactSize(size: number) {
		return (value: any[] | string) => {
			return value.length >= size || `${t("error.minCharacterCountStart")} [${size}] ${t("error.minCharacterCountEnd")}`
		}
	}

	return {
		minCharacterCount,
		required,
		date,
		integer,
		number,
		telephoneSabet,
		validSSN,
		hasPersianWord,
		email,
		time,
		percentage,
		exactSize,
		postalCode,
		phoneNumber,
		password,
		passwordRepeat,
	}
})
